import { notify } from '../../../../components/component-items/helpers'

const DATE_ACTION_FIELDS = new Set(['ship_before', 'ship_after', 'delivery_date'])

const toCanonicalDate = (value: any) => {
  if (!value) {
    return value
  }

  if (value instanceof Date) {
    if (isNaN(value.getTime())) {
      return value
    }
    const day = String(value.getDate()).padStart(2, '0')
    const month = String(value.getMonth() + 1).padStart(2, '0')
    const year = value.getFullYear()
    return `${year}-${month}-${day}`
  }

  const normalized = String(value).trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    return normalized
  }

  const ddmmyyyyMatch = normalized.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (!ddmmyyyyMatch) {
    return value
  }

  const [, day, month, year] = ddmmyyyyMatch
  return `${year}-${month}-${day}`
}

const normalizeDateActionValue = (action: any): any => {
  if (!action) {
    return action
  }

  if (action.type === 'logical_and' || action.type === 'logical_or' || action.type === 'logical_not') {
    return {
      ...action,
      logical: Array.isArray(action.logical)
        ? action.logical.map((logicalAction: any) => normalizeDateActionValue(logicalAction))
        : action.logical,
    }
  }

  const fieldType = action?.result?.fieldType
  if (!DATE_ACTION_FIELDS.has(fieldType)) {
    return action
  }

  return {
    ...action,
    result: {
      ...action.result,
      fieldValue: toCanonicalDate(action.result?.fieldValue),
    },
  }
}

const normalizeNotificationRecipients = (notificationTo: any) => {
  if (!notificationTo) {
    return []
  }
  if (typeof notificationTo === 'string') {
    return notificationTo
      .split(',')
      .map((recipient: string) => recipient?.trim())
      .filter(Boolean)
  }
  if (!Array.isArray(notificationTo)) {
    return []
  }
  return notificationTo
    .map((user: any) => {
      if (!user) {
        return null
      }
      if (typeof user === 'string') {
        return user
      }
      return user.display_name ?? user.value ?? user.label ?? null
    })
    .filter(Boolean)
}

const normalizeEditItemFieldType = (fieldType: any) => {
  if (fieldType && typeof fieldType === 'object' && 'value' in fieldType) {
    return fieldType.value
  }

  return fieldType
}

const EDIT_ITEM_ACTION_TYPES = new Set(['edit_order_item', 'edit_asn_item', 'edit_return_item'])

export const filterActions = (actions: any) => {
  var action = actions
  var logical = action.logical

  // TODO - update fieldValue to use field based on automationType

  // Update send_notification message logic to pull content from mentionRef
  if (action.type === 'send_notification') {
    const messageRef = actions.result?.messageRef
    const messageFromRef = messageRef?.current?.getContent ? messageRef.current.getContent() : null
    const message = (messageFromRef ?? action.result.fieldValue ?? '').trimEnd()

    if (!message) {
      notify({ title: 'Message Field Required', message: 'Missing notification body', type: 'error' })
      return
    }
    action = { ...action, result: { ...action.result, fieldValue: message } }
  } else if (action.type === 'logical_and' || action.type === 'logical_or' || action.type === 'logical_not') {
    // Only validate messages for nested send_notification actions
    for (let i = 0; i < logical.length; i++) {
      if (logical[i].type === 'send_notification') {
        const messageRef = logical[i].result?.messageRef
        const messageFromRef = messageRef?.current?.getContent ? messageRef.current.getContent() : null
        const message = (messageFromRef ?? logical[i].result?.fieldValue ?? '').trimEnd()
        if (!message) {
          notify({ title: 'Message Field Required', message: 'Missing notification body', type: 'error' })
          return
        }
        logical[i].result.fieldValue = message
      }
    }
    action = { ...action, logical: logical }
  }

  // Update edit-item action logic to use value from fieldValue
  if (EDIT_ITEM_ACTION_TYPES.has(action.type)) {
    let fieldType = action.result.fieldType
    action = { ...action, result: { ...action.result, fieldType: normalizeEditItemFieldType(fieldType) } }
  } else if (action.type === 'logical_and' || action.type === 'logical_or' || action.type === 'logical_not') {
    for (let i = 0; i < logical.length; i++) {
      if (EDIT_ITEM_ACTION_TYPES.has(logical[i].type)) {
        let fieldType = logical[i].result.fieldType
        logical[i].result.fieldType = normalizeEditItemFieldType(fieldType)
      }
    }
    action = { ...action, logical: logical }
  }

  // Update notificationTo
  if (action.type === 'send_notification') {
    const notificationTo = normalizeNotificationRecipients(action.result?.notificationTo)
    action = { ...action, result: { ...action.result, notificationTo } }
  } else if (action.type === 'logical_and' || action.type === 'logical_or' || action.type === 'logical_not') {
    for (let i = 0; i < logical.length; i++) {
      if (logical[i].type === 'send_notification') {
        logical[i].result.notificationTo = normalizeNotificationRecipients(logical[i].result.notificationTo)
      }
    }
    action = { ...action, logical }
  }

  action = normalizeDateActionValue(action)

  return action
}

export const getActionTypes = (userIsWarehouse: boolean) => [
  { label: 'Order', value: 'order' },
  { label: 'ASN', value: 'asn' },
  { label: 'Return', value: 'return' },
  // { label: 'Inventory', value: 'inventory' },
  { label: 'Product Variant', value: 'product_variant' },
  ...(userIsWarehouse ? [{ label: 'Billing', value: 'billing' }] : []),
]

export const getDefaultConfigForType = (value: string) => {
  const defaultFilters = { type: '', result: { fieldType: '', fieldValue: '' } }
  const eventDefault = { category: 'now', conditions: '' }

  let filters = defaultFilters
  let actions = defaultFilters
  let event = eventDefault

  if (value === 'order') {
    filters = { ...defaultFilters, type: 'all_orders' }
    actions = { ...defaultFilters, type: 'edit_order_field' }
  } else if (value === 'asn') {
    filters = { ...defaultFilters, type: 'all_asns' }
    actions = { ...defaultFilters, type: 'edit_asn_field' }
  } else if (value === 'return') {
    filters = { ...defaultFilters, type: 'all_returns' }
    actions = { ...defaultFilters, type: 'edit_return_field' }
  } else if (value === 'inventory') {
    filters = { ...defaultFilters, type: 'all_inventory_items' }
    actions = { ...defaultFilters, type: 'send_notification' }
    event = { ...event, category: 'inventory_created' }
  } else if (value === 'product_variant') {
    filters = { ...defaultFilters, type: 'all_product_variants' }
    actions = { ...defaultFilters, type: 'send_notification' }
  } else if (value === 'billing') {
    filters = { ...defaultFilters, type: 'all_billing_transactions' }
    actions = { ...defaultFilters, type: 'send_notification' }
    event = { ...event, category: 'billing_transaction_created' }
  }

  return { filters, actions, event }
}
